// import Swiper JS
import Swiper from "swiper";
import { Pagination, Autoplay } from "swiper/modules";
// import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

// gsap stuff
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// controllers
import Slider from "./controllers/sliderclick_controller";
import WindowHeight from "./controllers/windowheight_controller";
import Tile from "./controllers/tile_controller";
import MediaBlock from "./controllers/mediaBlock";
import GalleryModal from "./controllers/galleryModal";
import Search from "./controllers/search";
import Nav from "./controllers/nav";
import WordWrap from "./controllers/wordWrap";
import inView from "./controllers/inView";
import parallax from "./controllers/parallax";
import Stats from "./controllers/stats_controller";
import ChartDoughnut from "./controllers/chartDoughnut";
import ChartStat from "./controllers/chartStat";
import Profiles from "./controllers/profiles_controller";
import Cards from "./controllers/cards";
import DynamicFont from "./controllers/dynamicfont_controller";

document.addEventListener("DOMContentLoaded", function () {
  /*----------------*/
  /* Mega Menu */
  /*----------------*/

  // Adjust the position of the MegaMenu
  const adjustMegaMenuPosition = (parent) => {
    const megaMenu = parent.querySelector(".nav-item__container.isMegaMenu");
    if (megaMenu) {
      const megaMenuOffset = megaMenu.getBoundingClientRect();
      megaMenu.style.left = `-${megaMenuOffset.left}px`;
    }
  };

  // Reset styles for all MegaMenus
  const resetMegaMenuStyles = () => {
    document
      .querySelectorAll(".nav-item__container.isMegaMenu")
      .forEach((menu) => {
        menu.removeAttribute("style");
      });
  };

  // Initialize hover and keyboard interactions for MegaMenus
  const initializeMegaMenuInteractions = () => {
    document
      .querySelectorAll(".nav__item .nav-item__container.isMegaMenu")
      .forEach((menu) => {
        const parent = menu.closest(".nav__item");
        const menuLink = parent.querySelector(".nav__link--with-child");

        if (!parent || !menuLink) return;

        // Hover interactions
        parent.addEventListener("mouseenter", () => {
          parent.classList.add("hover");
          adjustMegaMenuPosition(parent);
        });

        parent.addEventListener("mouseleave", () => {
          parent.classList.remove("hover");
          resetMegaMenuStyles();
        });

        // Listen for keyboard-triggered menu opening from nav.js
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "aria-expanded"
            ) {
              const isExpanded =
                menuLink.getAttribute("aria-expanded") === "true";
              if (isExpanded) {
                // Menu opened via keyboard - apply positioning
                adjustMegaMenuPosition(parent);
              } else {
                // Menu closed - reset styles if not hovering
                if (!parent.classList.contains("hover")) {
                  resetMegaMenuStyles();
                }
              }
            }
          });
        });

        observer.observe(menuLink, {
          attributes: true,
          attributeFilter: ["aria-expanded"],
        });
      });
  };

  // Initialize MegaMenu functionality
  initializeMegaMenuInteractions();

  /*----------------*/
  /* Header search (mobile) */
  /*----------------*/

  // Open the search container
  document
    .querySelector(".nav .nav__burger_search")
    .addEventListener("click", () => {
      document
        .querySelector(".nav .nav__search-container")
        .classList.add("open");
    });

  /*----------------*/
  /* Mobile Menu */
  /*----------------*/

  // Focus trap management
  let focusTrapHandler = null;

  const focusTrap = (container) => {
    // Remove existing handler if any
    if (focusTrapHandler) {
      document.removeEventListener("keydown", focusTrapHandler);
    }

    focusTrapHandler = (e) => {
      if (e.key !== "Tab") return;

      // Get currently visible focusable elements
      const getVisibleFocusableElements = () => {
        // Check if we're in a submenu
        const activeSubmenu = container.querySelector(".level-2.active");
        const searchScope =
          activeSubmenu || container.querySelector(".level-1");

        if (!searchScope) return [];

        const elements = searchScope.querySelectorAll(
          "a, button, input, select, textarea, [tabindex]"
        );
        return Array.from(elements).filter((el) => {
          return (
            el.tabIndex !== -1 &&
            el.offsetWidth > 0 &&
            el.offsetHeight > 0 &&
            !el.disabled
          );
        });
      };

      const focusableElements = getVisibleFocusableElements();

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentElement = document.activeElement;

      // Check if current focus is within our container
      if (!container.contains(currentElement)) {
        e.preventDefault();
        firstElement.focus();
        return;
      }

      if (e.shiftKey) {
        // Shift + Tab
        if (currentElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (currentElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", focusTrapHandler);
  };

  const removeFocusTrap = () => {
    if (focusTrapHandler) {
      document.removeEventListener("keydown", focusTrapHandler);
      focusTrapHandler = null;
    }
  };

  // Manage search container focusable elements
  const manageSearchFocus = (disable) => {
    const searchContainer = document.querySelector(".nav__search-container");
    if (!searchContainer) return;

    const focusableElements = searchContainer.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    focusableElements.forEach((el) => {
      if (disable) {
        if (!el.hasAttribute("data-original-tabindex")) {
          el.setAttribute("data-original-tabindex", el.tabIndex);
        }
        el.tabIndex = -1;
      } else {
        if (el.hasAttribute("data-original-tabindex")) {
          el.tabIndex = parseInt(el.getAttribute("data-original-tabindex"));
          el.removeAttribute("data-original-tabindex");
        } else {
          el.tabIndex = 0;
        }
      }
    });
  };

  // Toggle the menu and sidebar visibility
  const toggleMenuAndSidebar = () => {
    const burger = document.querySelector(".nav__burger .menu-sidebar_burger");
    const sidebarNav = document.querySelector(
      ".nav .nav__mobile_menu-container .sidebar-navigation"
    );
    const mobileMenuContainer = document.querySelector(
      ".nav__mobile_menu-container"
    );

    if (!burger || !sidebarNav) return;

    burger.addEventListener("click", function () {
      const isOpen = this.parentElement.classList.contains("activeMenu");

      this.parentElement.classList.toggle("activeMenu");
      document.body.classList.toggle("menuOpen");

      sidebarNav.style.display = !isOpen ? "block" : "none";

      if (!isOpen) {
        // Menu opening
        focusTrap(mobileMenuContainer);
        manageSearchFocus(true);
        document.addEventListener("keydown", handleEscapeKey);

        // Focus first menu item
        setTimeout(() => {
          const firstMenuItem =
            mobileMenuContainer.querySelector(".level-1 > li > a");
          if (firstMenuItem) {
            firstMenuItem.focus();
          }
        }, 50);
      } else {
        // Menu closing
        removeFocusTrap();
        manageSearchFocus(false);
        document.removeEventListener("keydown", handleEscapeKey);
        currentParentMenuItem = null; // Clear parent menu item reference
        this.focus();
      }
    });
  };

  // Close sidebar-navigation on resize above 1024px
  const closeSidebarOnResize = () => {
    const sidebarNav = document.querySelector(
      ".nav .nav__mobile_menu-container .sidebar-navigation"
    );

    window.addEventListener("resize", () => {
      if (window.innerWidth > 1024) {
        document.querySelectorAll(".activeMenu").forEach((element) => {
          element.classList.remove("activeMenu");
        });

        document.body.classList.remove("menuOpen");

        if (sidebarNav) {
          sidebarNav.style.display = "none";
        }

        // Clean up focus management
        removeFocusTrap();
        manageSearchFocus(false);
        document.removeEventListener("keydown", handleEscapeKey);
        currentParentMenuItem = null; // Clear parent menu item reference
      }
    });
  };

  // Escape key handler
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      const burger = document.querySelector(
        ".nav__burger .menu-sidebar_burger"
      );
      const activeSubmenu = document.querySelector(
        ".nav__mobile_menu-container .level-2.active"
      );

      if (activeSubmenu) {
        // Close submenu and return to level-1
        const backButton = activeSubmenu.querySelector(".back a");
        if (backButton) {
          backButton.click(); // This will trigger the focus restoration in the click handler
        }
      } else if (
        burger &&
        burger.parentElement.classList.contains("activeMenu")
      ) {
        // Close entire menu
        burger.click();
      }
    }
  };

  // Track the parent menu item that opened current submenu
  let currentParentMenuItem = null;

  // Handle mobile navigation item clicks
  const handleMobileNavigationClicks = () => {
    document
      .querySelectorAll(".nav__mobile_menu-container .mobileNav li a")
      .forEach((link) => {
        link.addEventListener("click", (event) => {
          const currentMenu = link.closest("ul");
          const currentListItem = link.parentElement;
          const parentLevel = link.closest("ul").dataset.level || 1;
          const parentMenu = document.querySelector(`.level-${parentLevel}`);
          const subMenu = link.nextElementSibling;

          if (currentListItem.classList.contains("back")) {
            // Handle back button
            currentMenu.classList.remove("active");
            if (parentMenu) parentMenu.classList.remove("hidden");
            event.preventDefault();
            document
              .querySelector(".nav .nav__item-container")
              .classList.remove("hidden");

            // Restore focus to the parent menu item that opened this submenu
            if (currentParentMenuItem) {
              setTimeout(() => {
                currentParentMenuItem.focus();
                currentParentMenuItem = null; // Clear the reference
              }, 50);
            }
          } else if (currentListItem.querySelector("ul")) {
            // Handle expanding submenu
            if (subMenu) {
              // Store reference to the parent menu item
              currentParentMenuItem = link;

              subMenu.classList.add("active");

              // Auto-focus first menu item (not back button)
              setTimeout(() => {
                const firstMenuItem = subMenu.querySelector("li:not(.back) a");
                if (firstMenuItem) {
                  firstMenuItem.focus();
                }
              }, 50);
            }
            currentMenu.classList.add("hidden");
            event.preventDefault();
            document
              .querySelector(".nav .nav__item-container")
              .classList.add("hidden");
          }
        });
      });
  };

  // Initialize navigation behavior
  const initializeNavigation = () => {
    toggleMenuAndSidebar();
    closeSidebarOnResize();
    handleMobileNavigationClicks();
  };

  // Initialize the script
  initializeNavigation();

  /*----------------*/
  /* Home Slider */
  /*----------------*/

  // Check if swiper element exists before initializing
  const swiperElement = document.querySelector(".swiper");

  if (swiperElement) {
    // init Swiper:
    const swiper = new Swiper(".swiper", {
      modules: [Pagination, Autoplay],
      spaceBetween: 0,
      // autoplay: {
      //   delay: 5000,
      // },
      loop: true,
      speed: 500,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
        type: "progressbar",
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: false, // We're handling ARIA manually for better control
      },
    });

    // Focus management for autoplay
    swiper.el.addEventListener("focusin", () => {
      swiper.autoplay.stop();
    });

    swiper.el.addEventListener("focusout", (e) => {
      // Check if focus is still within slider
      setTimeout(() => {
        if (!swiper.el.contains(document.activeElement)) {
          swiper.autoplay.start();
        }
      }, 100);
    });

    // Custom Dot Pagination Trigger
    const dots = document.querySelectorAll(".banner_items__dot");

    dots.forEach((dot, index) => {
      // Handle both click and keyboard events
      const handleDotActivation = () => {
        // Navigate to the slide corresponding to the clicked dot
        swiper.slideTo(index);
      };

      dot.addEventListener("click", handleDotActivation);

      // Add keyboard support for Enter and Space
      dot.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleDotActivation();
        }
      });
    });

    // Function to manage slide visibility and focusable elements
    const updateSlideVisibility = () => {
      swiper.slides.forEach((slide, index) => {
        const isActive = index === swiper.activeIndex;
        slide.setAttribute("aria-hidden", !isActive);

        // Manage focusable elements within slides
        const focusableElements = slide.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        focusableElements.forEach((el) => {
          if (isActive) {
            // Restore original tabindex or set to 0
            if (el.hasAttribute("data-original-tabindex")) {
              el.tabIndex = parseInt(el.getAttribute("data-original-tabindex"));
            } else {
              el.tabIndex = 0;
            }
          } else {
            // Store original tabindex if not already stored
            if (!el.hasAttribute("data-original-tabindex")) {
              el.setAttribute("data-original-tabindex", el.tabIndex);
            }
            el.tabIndex = -1;
          }
        });
      });
    };

    // Initialize slide visibility after swiper is ready
    swiper.on("init", updateSlideVisibility);
    swiper.on("afterInit", updateSlideVisibility);

    // Also run it immediately if swiper is already initialized
    if (swiper.initialized) {
      updateSlideVisibility();
    }

    // Update the active dot class and ARIA attributes on slide change
    swiper.on("slideChange", function () {
      const activeIndex = swiper.realIndex; // Get the current slide index
      const announcement = document.getElementById("slider-announcements");

      // Update slide visibility
      updateSlideVisibility();

      dots.forEach((dot, index) => {
        if (index === activeIndex) {
          dot.classList.add("active");
          dot.setAttribute("aria-selected", "true");
        } else {
          dot.classList.remove("active");
          dot.setAttribute("aria-selected", "false");
        }
      });

      // Announce slide change to screen readers
      if (announcement) {
        const slideText =
          dots[activeIndex]?.querySelector(".tab_text")?.textContent ||
          `Slide ${activeIndex + 1}`;
        announcement.textContent = `Now showing: ${slideText}`;
      }
    });
  } // End of swiper conditional block

  /*----------------*/
  /* GSAP - Basic Setup */
  /*----------------*/

  gsap.registerPlugin(ScrollTrigger);

  /*----------------*/
  /* GSAP - Animated Headings */
  /*----------------*/

  /*
  const animatedHeadings = document.querySelectorAll('.reveal-h');

  animatedHeadings.forEach(animatedHeading => {
    let animateUpTimeline = gsap.timeline({
      scrollTrigger:{
        trigger: animatedHeading,
        start: "top+=100 bottom",
        // end: "bottom bottom",
        // markers:true,
        // scrub: true,
        toggleActions: "play none none none",
        once: true
      }
    });

    animateUpTimeline.fromTo(animatedHeading, {y:50, opacity: 0, duration: 0.5}, {y: 0, opacity: 1, duration: 0.5});
  });
  */

  /*----------------*/
  /* GSAP - Parallax Header Banners */
  /*----------------*/

  /*
  Replace with dynamic controller below
  ///
  const parallaxHeaders = document.querySelectorAll('[data-controller="opparallax"]');

  parallaxHeaders.forEach(parallaxHeader => {
    parallaxHeader.classList.add('has-parallax');
    const bg = parallaxHeader.querySelector('.header-image__bg, .header-main__bg');

    let parallaxHeaderTimeline = gsap.timeline({
      scrollTrigger:{
        trigger: parallaxHeader,
        start: "top top",
        end: "bottom top",
        // markers:true,
        scrub: true,
        toggleActions: "play none none reverse",
      }
    });

    parallaxHeaderTimeline.fromTo(bg, {y:0}, {y: 70});
  });
  */

  const parallaxElements = document.querySelectorAll(
    '[data-controller~="opparallax"]'
  );
  const parallaxs = Array.from(parallaxElements).map(
    (element) => new parallax(element)
  );

  /*----------------*/
  /* Window Height */
  /*----------------*/

  const windowHeightElements = document.querySelectorAll(
    '[data-controller~="windowheight"]'
  );
  const windowHeights = Array.from(windowHeightElements).map(
    (element) => new WindowHeight(element)
  );

  /*----------------*/
  /* Profiles */
  /*----------------*/

  const profileElements = document.querySelectorAll(
    '[data-controller~="profiles"]'
  );
  const profiles = Array.from(profileElements).map(
    (element) => new Profiles(element)
  );

  /*----------------*/
  /* Slider */
  /*----------------*/

  /*
  const sliderElement = document.querySelector('.slider');
  if (sliderElement) {
    const slider = new Slider(sliderElement);
  } else {
    console.error('Slider element not found!');
  }
  */

  const slider = new Slider(document.querySelector(".slider"));

  /*
  const swiperSlider = new Swiper('.slider__wrap', {
    modules: [Pagination, Navigation],
    // loop: true,
    // slidesPerView: 1
    // slidesPerView: 1,     // Shows one full slide + part of the next
    // centeredSlides: true,    // Centers the active slide
    spaceBetween: 0,        // Adds space between slides
    loop: true,              // Enables looping
    pagination: {
      el: '.swiper-pagination', // Target the pagination container
      clickable: true,          // Makes pagination bullets clickable
      type: 'progressbar',
    },
  });
  */

  /*----------------*/
  /* Tiles */
  /*----------------*/

  /*
  // temp disabled. However effect seems to heavy i.e. too much movement
  const tileElements = document.querySelectorAll('[data-controller~="tile"]');
  const tiles = Array.from(tileElements).map((element) => new Tile(element));
  */

  /*----------------*/
  /* Journal Elements */
  /*----------------*/

  // Initial setup
  inView();

  /*----------------*/
  /* Media Block */
  /*----------------*/

  // Initialize the MediaBlock on a specific element
  const mediaBlockElements = document.querySelectorAll(".media-block");
  const mediaBlocks = Array.from(mediaBlockElements).map(
    (element) => new MediaBlock(element)
  );

  // Optional: Add global cleanup on page unload for all instances
  window.addEventListener("unload", () => {
    mediaBlocks.forEach((mediaBlock) => mediaBlock.disconnect());
  });

  /*----------------*/
  /* Gallery Modal */
  /*----------------*/

  const galleryModalElements = document.querySelectorAll(".gallery");
  const galleryModals = Array.from(galleryModalElements).map(
    (element) => new GalleryModal(element)
  );

  /*----------------*/
  /* Nav */
  /*----------------*/

  const nav = document.querySelector("nav");
  if (nav) {
    new Nav(nav);
  }

  /*----------------*/
  /* Search Input Box */
  /*----------------*/

  const searchElement = document.querySelector(".search");
  if (searchElement) {
    const searchInstance = new Search(searchElement);
  }

  /*----------------*/
  /* Word Wrap */
  /*----------------*/

  const wordWrapElements = document.querySelectorAll("[word-wrap]");
  wordWrapElements.forEach((el) => new WordWrap(el));

  /*----------------*/
  /* Stats */
  /*----------------*/
  const statsElement = document.querySelector("[stats]");
  if (statsElement) {
    const stats = new Stats(statsElement);
  }

  /*----------------*/
  /* Charts */
  /*----------------*/

  // doughnut
  const chartDoughnutsElements = document.querySelectorAll(".chart-donut");
  chartDoughnutsElements.forEach((el) => new ChartDoughnut(el));

  // stats
  const chartStatsElements = document.querySelectorAll(".chart-statistic");
  chartStatsElements.forEach((el) => new ChartStat(el));

  /*----------------*/
  /* Cards */
  /*----------------*/

  const cardsElements = document.querySelectorAll('[data-controller~="cards"]');
  const cards = Array.from(cardsElements).map((element) => new Cards(element));

  /*----------------*/
  /* Dynamic Font Sizing */
  /*----------------*/

  const dynamicFontElements = document.querySelectorAll(
    '[data-controller~="dynamicfont"]'
  );
  const dynamicFonts = Array.from(dynamicFontElements).map(
    (element) => new DynamicFont(element)
  );
});
